import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableContainer,
  Link,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { format } from 'date-fns';
import { I18nContext } from 'translations';
import { PREPACKAGES_STATUSES, PAYMENT_METHODS, SIZE_CD } from 'utils/constants';
import { prepackagesStatusClass, formatCostCompact } from 'utils';
import EmptyData from '../EmptyData';
import styles from '../style';
import { cancelPrepackage } from 'context/prepackages/actions';
import { useStateValue } from 'context/store';
import ModalPrepackages from 'components/Modals/ModalPrepackages';
import ModalPrepackageCustomer from 'components/Modals/ModalPrepackageCustomer';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import TimerOffIcon from '@material-ui/icons/TimerOff';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import FlagIcon from '@material-ui/icons/Flag';

const EXPIRED_STATUS = 10;
const ID_SHORT_LENGTH = 8;

const RouteLine = ({ icon, address }) => (
  <Tooltip title={address || ''} placement="top-start">
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 340 }}>
      {icon}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {address}
      </span>
    </div>
  </Tooltip>
);

const copyToClipboard = (text) => {
  if (navigator.clipboard) navigator.clipboard.writeText(text);
};

const PrepackageTable = ({
  classes,
  data,
  recordsPerPage,
  getPrepackages,
  user
}) => {
  const [, dispatch] = useStateValue();
  const [currentPage, setCurrentPage] = useState(data.page);
  const { translate } = useContext(I18nContext);
  const [prepackageUpdate, setPrepackageUpdate] = useState(null);
  const [contactsPkg, setContactsPkg] = useState(null);

  const handleCancelClick = async (prePackageId) => {
    await cancelPrepackage(dispatch, prePackageId, user);
  };

  const handleUpdate = (id) => {
    setPrepackageUpdate(id);
  }
  const handleClose = () => {
    setPrepackageUpdate(null)
  }

  useEffect(() => {
    setCurrentPage(data.page);
  }, [data]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    getPrepackages(newPage, user);
  };

  if (!data || (data && data.data.length <= 0)) {
    return <EmptyData message={translate('thereIsNoData')} />;
  }

  return (
    <>
      <TableContainer>
        <Table className={classes.tableStyle} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{translate('prepakages.table.status')}</TableCell>
              <TableCell>{translate('prepackages.table.date')}</TableCell>
              <TableCell>{translate('prepackages.table.route')}</TableCell>
              <TableCell>{translate('prepakages.table.orderNumber')}</TableCell>
              <TableCell>{translate('prepackages.table.size')}</TableCell>
              <TableCell>{translate('prepackages.table.deliveryCost')}</TableCell>
              <TableCell>{translate('prepackages.table.conditions')}</TableCell>
              <TableCell>{translate('prepakages.table.id')}</TableCell>
              <TableCell>{translate('prepackages.table.service')}</TableCell>
              <TableCell>{translate('prepakages.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.data || []).map((pkg, index) => (
              <TableRow
                hover
                key={pkg.id}
              >
                <TableCell>
                  <span className={prepackagesStatusClass(pkg.status_cd)}>
                    {pkg.status_cd === EXPIRED_STATUS && <TimerOffIcon fontSize="small" />}
                    {translate(PREPACKAGES_STATUSES[pkg.status_cd])}
                  </span>
                </TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {pkg.created_at && (
                    <>
                      <div>{format(new Date(pkg.created_at), 'dd/MM/yyyy')}</div>
                      <Typography variant="caption" color="textSecondary">
                        {format(new Date(pkg.created_at), 'hh:mm a')}
                      </Typography>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <RouteLine
                    icon={<MyLocationIcon fontSize="small" style={{ color: '#2e7d32' }} />}
                    address={pkg.origin_address}
                  />
                  <RouteLine
                    icon={<FlagIcon fontSize="small" style={{ color: '#c62828' }} />}
                    address={pkg.end_address}
                  />
                </TableCell>
                <TableCell>
                  {pkg.reference}
                </TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {SIZE_CD[pkg.size_cd] ? translate(SIZE_CD[pkg.size_cd]) : '-'}
                </TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  <div>{formatCostCompact(pkg.delivery_cost) || '-'}</div>
                  <Typography variant="caption" color="textSecondary">
                    {PAYMENT_METHODS[pkg.payment_method_cd] ? translate(PAYMENT_METHODS[pkg.payment_method_cd]) : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {pkg.counter_delivery && (
                    <div style={{ marginBottom: 4 }}>
                      <span className="status-tag-style status-collect-style">
                        {translate('prepackages.table.mustCollect')}
                        {pkg.collected_value && ` · ${formatCostCompact(pkg.collected_value)}`}
                      </span>
                    </div>
                  )}
                  {pkg.requires_a_driver_with_base_money && (
                    <div>
                      <span className="status-tag-style status-base-style">
                        {translate('prepackages.table.requiresBase')}
                        {pkg.money_to_pay_for_package && ` · ${formatCostCompact(pkg.money_to_pay_for_package)}`}
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={`${pkg.id} · ${translate('prepackages.table.copyId')}`}>
                    <span
                      style={{ fontFamily: 'monospace', cursor: 'pointer' }}
                      onClick={() => copyToClipboard(pkg.id)}
                    >
                      …{pkg.id.slice(-ID_SHORT_LENGTH)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {pkg.booking_id ? (
                    <Link
                      color="primary"
                      href={`/bookings/${pkg.booking_id}?company_id=${pkg.company_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      {translate('prepackages.table.viewService')}
                      <OpenInNewIcon fontSize="inherit" />
                    </Link>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    disabled={!pkg.customer_info && !pkg.origin_contact}
                    onClick={() => setContactsPkg(pkg)}
                  >
                    <PersonOutlineIcon />
                  </IconButton>
                  { pkg.status_cd === 0 && (
                    <>
                      <IconButton color="primary" onClick={() => handleUpdate(pkg.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="secondary" onClick={() => handleCancelClick(pkg.id)}>
                        <DeleteIcon/>
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.total ? data.total : data.pages * recordsPerPage}
        page={currentPage - 1}
        rowsPerPageOptions={[]}
        rowsPerPage={recordsPerPage}
        onChangePage={(event, newPage) => handleChangePage(event, newPage + 1)}
      />
      {contactsPkg &&
        <ModalPrepackageCustomer
          open={!!contactsPkg}
          handleClose={() => setContactsPkg(null)}
          customer={contactsPkg.customer_info}
          originContact={contactsPkg.origin_contact}
        />
      }
      {prepackageUpdate &&
        <ModalPrepackages
          handleClose={handleClose}
          open={!!prepackageUpdate}
          id={prepackageUpdate}
        />
      }
    </>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.data === nextProps.data;
}

export default withStyles(styles)(React.memo(PrepackageTable, areEqual));
